<script setup lang="ts">
import { Line } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement,
    Filler,
    type Plugin,
} from 'chart.js';
import { computed } from 'vue';
import type { LineChartDataType, LineChartOptionsType } from '@/types/ChartType';
import { createHtmlLegendPlugin } from '@/utils/chartLegendPlugin';
import { BChartSkeleton, BChartEmptyState } from '@/design-system';

type PropsType = {
    data: LineChartDataType;
    options?: LineChartOptionsType;
    height?: number;
    width?: number;
    loading?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    height: 400,
    width: undefined,
    loading: false,
});

ChartJS.register(Title, Tooltip, Legend, LineElement, LinearScale, CategoryScale, PointElement, Filler);

const defaultOptions = computed(
    (): LineChartOptionsType => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'start',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        family: 'Roboto, sans-serif',
                        size: 12,
                    },
                },
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: 'white',
                bodyColor: 'white',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                padding: 12,
            },
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
        },
        scales: {
            x: {
                display: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawOnChartArea: true,
                    drawTicks: true,
                },
                ticks: {
                    font: {
                        family: 'Roboto, sans-serif',
                        size: 11,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    maxRotation: 45,
                    minRotation: 0,
                },
            },
            y: {
                display: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawOnChartArea: true,
                    drawTicks: false,
                },
                ticks: {
                    font: {
                        family: 'Roboto, sans-serif',
                        size: 11,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    padding: 8,
                },
            },
        },
        elements: {
            point: {
                radius: 4,
                hoverRadius: 8,
                borderWidth: 2,
            },
            line: {
                borderWidth: 2,
                tension: 0.1,
            },
        },
    }),
);

const legendSpacingPlugin: Plugin = {
    id: 'legendSpacing',
    beforeInit(chart: InstanceType<typeof ChartJS>) {
        const legend = (chart as any).legend;
        if (!legend) {
            return;
        }
        const originalFit = legend.fit.bind(legend);
        legend.fit = function fit() {
            originalFit();
            this.height += 12;
        };
    },
};

const chartPlugins = computed(() => {
    const plugins = [legendSpacingPlugin];
    if (props.options?.plugins?.htmlLegend) {
        const htmlLegendPlugin = createHtmlLegendPlugin(props.options.plugins.htmlLegend);
        plugins.push(htmlLegendPlugin);
    }

    return plugins;
});

const mergedOptions = computed(
    (): LineChartOptionsType => ({
        ...defaultOptions.value,
        ...props.options,
        plugins: {
            ...defaultOptions.value.plugins,
            ...props.options?.plugins,
        },
        scales: {
            ...defaultOptions.value.scales,
            ...props.options?.scales,
        },
    }),
);

const containerStyle = computed(() => ({
    height: `${props.height}px`,
    width: props.width ? `${props.width}px` : '100%',
}));

const hasData = computed(() => props.data.datasets.length > 0);
</script>

<template>
    <div class="b-line-chart">
        <BChartSkeleton
            v-if="props.loading"
            :height="props.height"
        />
        <BChartEmptyState
            v-else-if="!hasData"
            :height="props.height"
        />
        <div
            v-else
            :style="containerStyle"
        >
            <Line
                :data="data"
                :options="mergedOptions"
                :plugins="chartPlugins"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.b-line-chart {
    position: relative;
    max-width: 100%;
    overflow: hidden;
}
</style>
